import { XorOfUnions } from "@redotech/util/type";
import { RegistrationRewardType } from "../extended-warranty";
import { CustomerCharacteristicType } from "../marketing/segments/segment-types";
import { Provider } from "../order";
import { Team } from "../team";
import {
  Integration,
  RechargeIntegrationTrigger,
  RechargeIntegrationTriggerKey,
  rechargeIntegrationTriggers,
  YotpoIntegrationTrigger,
  YotpoIntegrationTriggerKey,
  yotpoIntegrationTriggers,
} from "./integration-triggers";
import { RuleTriggerType } from "./rules/types";
import { SchemaType } from "./schemas/schemas";

export const categories = [
  "Order tracking",
  "Return tracking",
  "Try first",
  "Marketing",
  "Reviews",
  "Upsell",
  "Oms",
  "Checkout personalization",
  "Checkout package",
  "Post-purchase",
  "Tracking page",
  "Extended warranty",
  "Integration",
  "Recover",
  "Rules",
  "Custom Event",
] as const;
export type Category = (typeof categories)[number];

const categorySet = new Set<string>(categories);

export function isCategory(value: unknown): value is Category {
  return typeof value === "string" && categorySet.has(value);
}
export function categoryToSnakeCase(category: Category) {
  return category.toLowerCase().replace(" ", "_");
}
export function snakeCaseToCategory(snakeCase: string): Category {
  const lowercase = snakeCase.replace("_", " ");
  return (lowercase.charAt(0).toUpperCase() + lowercase.slice(1)) as Category;
}

export enum OrderTrackingTriggerKey {
  ORDER_CREATED = "order_created",
  ORDER_FULFILLED = "order_fulfilled",
  ORDER_PRE_TRANSIT = "order_pre_transit",
  ORDER_IN_TRANSIT = "order_in_transit",
  ORDER_OUT_FOR_DELIVERY = "order_out_for_delivery",
  ORDER_DELIVERED = "order_delivered",
  ORDER_AVAILABLE_FOR_PICKUP = "order_available_for_pickup",
  ORDER_AVAILABLE_FOR_PICKUP_CARRIER = "order_available_for_pickup_carrier",
  ORDER_STALLED_IN_TRANSIT = "order_stalled_in_transit",
  ORDER_STALLED_IN_FULFILLMENT = "order_stalled_in_fulfillment",
  ORDER_DELAYED = "order_delayed",
  ORDER_ARRIVING_EARLY = "order_arriving_early",
  ORDER_RETURN_TO_SENDER = "order_return_to_sender",
  ORDER_DELIVERY_ATTEMPTED = "order_delivery_attempted", // has this ever done anything??
  ORDER_DELIVERY_FAILURE = "order_delivery_failure",
  ORDER_SHIPMENT_CANCELLED = "order_shipment_cancelled",
  ORDER_SHIPMENT_ERROR = "order_shipment_error",
  POST_PURCHASE_UPSELL = "post_purchase_upsell",
}

export enum EwTriggerKey {
  EXTENDED_WARRANTY_PURCHASED = "extended_warranty_purchased",
}

export function isOrderTrackingTrigger(
  trigger: TriggerKey,
): trigger is OrderTrackingTriggerKey {
  return Object.values(OrderTrackingTriggerKey).includes(
    trigger as OrderTrackingTriggerKey,
  );
}

export enum ReturnTrackingTriggerKey {
  // easypost
  RETURN_REQUEST_CREATED = "return_created",
  RETURN_PRE_TRANSIT = "return_pre_transit",
  RETURN_IN_TRANSIT = "return_in_transit",
  RETURN_OUT_FOR_DELIVERY = "return_out_for_delivery",
  RETURN_DELIVERED = "return_delivered",
  RETURN_AVAILABLE_FOR_PICKUP = "return_available_for_pickup",
  RETURN_STALLED = "return_stalled",
  RETURN_DELAYED = "return_delayed",
  RETURN_ARRIVING_EARLY = "return_arriving_early",
  RETURN_RETURN_TO_SENDER = "return_return_to_sender",
  RETURN_DELIVERY_ATTEMPTED = "return_delivery_attempted",
  RETURN_DELIVERY_FAILURE = "return_delivery_failure",
  RETURN_SHIPMENT_CANCELLED = "return_shipment_cancelled",
  RETURN_SHIPMENT_ERROR = "return_shipment_error",
  RETURN_IN_REVIEW = "return_in_review",

  // redo
  // RETURN_RECEIVED = "return_received", // requirements unclear
  RETURN_APPROVED = "return_approved",
  RETURN_PROCESSED = "return_processed",
  RETURN_REJECTED = "return_rejected",
  // RETURN_ACCEPTED = "return_accepted", // requirements unclear
  // RETURN_EXCHANGE_ORDER_READY = "return_exchange_order_ready", // requirements unclear

  RETURN_PICKUP_RESCHEDULED_BY_MERCHANT = "return_pickup_rescheduled_by_merchant",
  RETURN_PICKUP_REMINDER = "return_pickup_reminder",
  RETURN_PICKUP_SUCCESS = "return_pickup_success",
  RETURN_PICKUP_FAILURE = "return_pickup_failure",
  RETURN_SHIPMENT_REMINDER = "return_shipment_reminder",
  RETURN_OUSTANDING_BALANCE = "return_outstanding_balance",
  RETURN_EXPIRED = "return_expired",
  RETURN_EXCHANGE_OUT_OF_STOCK = "return_exchange_out_of_stock",
  RETURN_EXCHANGE_COMPLETED = "return_exchange_completed",

  // External Return Shipping Providers
  BOXLESS_RETURNS_RMA_CREATED = "boxless_returns_rma_created",
}

export enum TnplTriggerKey {
  TRIAL_STARTED = "trial_started",
  ABOUT_TO_REAUTH_CUSTOMER = "about_to_reauth_customer",
  TRIAL_ITEMS_CANCELLED = "trial_items_cancelled",
  REAUTHORIZATION_FAILED = "reauthorization_failed",
  TRIAL_ENDED = "trial_ended",
}

export function isReturnTrackingTrigger(trigger: TriggerKey) {
  return Object.values(ReturnTrackingTriggerKey).includes(
    trigger as ReturnTrackingTriggerKey,
  );
}

export function isTnplTrackingTrigger(trigger: TriggerKey) {
  return Object.values(TnplTriggerKey).includes(trigger as TnplTriggerKey);
}

export function isEwTrigger(trigger: TriggerKey) {
  return Object.values(EwTriggerKey).includes(trigger as EwTriggerKey);
}

export enum MarketingTriggerKey {
  EMAIL_SIGNUP = "email_signup",
  EMAIL_SIGNUP_SHOPIFY = "email_signup_shopify",
  SMS_SIGNUP = "sms_confirmed",
  MARKETING_CAMPAIGN = "marketing_campaign",
  CART_ABANDONED = "cart_abandoned",
  BROWSE_ABANDONED = "browse_abandoned",
  CHECKOUT_ABANDONED = "checkout_abandoned",
  COMMENTSOLD_CART_ABANDONED = "COMMENTSOLD_CART_ABANDONED",
  COMMENTSOLD_BROWSE_ABANDONED = "COMMENTSOLD_BROWSE_ABANDONED",
  COMMENTSOLD_CHECKOUT_ABANDONED = "COMMENTSOLD_CHECKOUT_ABANDONED",
  BACK_IN_STOCK = "back_in_stock",
  CUSTOMER_GROUP_ENTERED = "customer_group_entered",
  CUSTOMER_GROUP_EXITED = "customer_group_exited",
  WARRANTY_REGISTRATION = "warranty_registration",
  LOW_INVENTORY = "low_inventory",
  DATE = "date",
  PRICE_DROP = "price_drop",
  OOS_EXCHANGE_RESTOCKED = "oos_exchange_restocked",
  REFUND_RETURN_SUBMITTED = "refund_return_submitted",
}

export function isMarketingTrigger(trigger: TriggerKey) {
  return (
    Object.values(MarketingTriggerKey).includes(
      trigger as MarketingTriggerKey,
    ) || trigger === OrderTrackingTriggerKey.ORDER_CREATED
  );
}

export function isRegistrationTrigger(trigger: TriggerKey) {
  return trigger === MarketingTriggerKey.WARRANTY_REGISTRATION;
}

export function isReturnsRecoverTrigger(trigger: TriggerKey) {
  // Triggers that route to the Returns queue (admin-only)
  return (
    trigger === MarketingTriggerKey.OOS_EXCHANGE_RESTOCKED ||
    trigger === MarketingTriggerKey.REFUND_RETURN_SUBMITTED
  );
}

const marketingDateTriggerFields = [
  CustomerCharacteristicType.BIRTHDAY,
] as const;
marketingDateTriggerFields satisfies readonly CustomerCharacteristicType[];
export type MarketingDateTriggerField =
  (typeof marketingDateTriggerFields)[number];

export enum ReviewsTriggerKey {
  REVIEW_SUBMITTED = "review_submitted",
}

export enum OmsTriggerKey {
  NEW_FULFILLMENT_GROUP = "new_fulfillment_group",
  ORDER_READY_FOR_PICKUP = "order_ready_for_pickup",
  ADDRESS_VERIFICATION_FAILED = "address_verification_failed",
}

export enum CheckoutOptimizationTriggerKey {
  CHECKOUT_VISIT = "checkout_visit",
}

export enum CheckoutParcelTriggerKey {
  CHECKOUT_VISIT_PARCEL = "checkout_visit_parcel",
}

export enum PostPurchaseTriggerKey {
  ORDER_COMPLETED = "order_completed",
}

export enum TrackingPageTriggerKey {
  TRACKING_PAGE_VIEW = "tracking_page_view",
}

export enum CustomEventTriggerKey {
  CUSTOM_EVENT = "custom_event",
}

export type TriggerKey =
  | OrderTrackingTriggerKey
  | ReturnTrackingTriggerKey
  | TnplTriggerKey
  | MarketingTriggerKey
  | ReviewsTriggerKey
  | OmsTriggerKey
  | CheckoutOptimizationTriggerKey
  | CheckoutParcelTriggerKey
  | PostPurchaseTriggerKey
  | TrackingPageTriggerKey
  | EwTriggerKey
  | YotpoIntegrationTriggerKey
  | RechargeIntegrationTriggerKey
  | CustomEventTriggerKey
  | RuleTriggerType;

export interface BaseTrigger {
  category: Category;
  key: TriggerKey;
  displayName: string;
  schemaType: SchemaType;
  description: string;
}
interface BaseMarketingTrigger extends BaseTrigger {
  category: "Marketing";
}

export type MarketingEmailSignupTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.EMAIL_SIGNUP;
};

export type MarketingEmailSignupShopifyTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY;
};

export type MarketingSmsSignupTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.SMS_SIGNUP;
};

export type MarketingCampaignTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.MARKETING_CAMPAIGN;
};

export type MarketingCartAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.CART_ABANDONED;
};

export type MarketingBrowseAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.BROWSE_ABANDONED;
};

export type MarketingCheckoutAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.CHECKOUT_ABANDONED;
};

export type MarketingCsCartAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED;
};

export type MarketingCsBrowseAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED;
};

export type MarketingCsCheckoutAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED;
};

export type MarketingBackInStockTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.BACK_IN_STOCK;
};

export type MarketingCustomerGroupEnteredTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.CUSTOMER_GROUP_ENTERED;
};

export type MarketingCustomerGroupExitedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.CUSTOMER_GROUP_EXITED;
};

export type MarketingWarrantyRegistrationTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.WARRANTY_REGISTRATION;
};

export type MarketingLowInventoryTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.LOW_INVENTORY;
};

export type MarketingDateTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.DATE;
};

export type MarketingPriceDropTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.PRICE_DROP;
};

export type MarketingOosExchangeRestockedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.OOS_EXCHANGE_RESTOCKED;
};

export type MarketingRefundReturnSubmittedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.REFUND_RETURN_SUBMITTED;
};

export type MarketingTrigger =
  | MarketingEmailSignupTrigger
  | MarketingEmailSignupShopifyTrigger
  | MarketingSmsSignupTrigger
  | MarketingCampaignTrigger
  | MarketingCartAbandonedTrigger
  | MarketingBrowseAbandonedTrigger
  | MarketingCheckoutAbandonedTrigger
  | MarketingCsCartAbandonedTrigger
  | MarketingCsBrowseAbandonedTrigger
  | MarketingCsCheckoutAbandonedTrigger
  | MarketingBackInStockTrigger
  | MarketingCustomerGroupEnteredTrigger
  | MarketingCustomerGroupExitedTrigger
  | MarketingWarrantyRegistrationTrigger
  | MarketingLowInventoryTrigger
  | MarketingDateTrigger
  | MarketingPriceDropTrigger
  | MarketingOosExchangeRestockedTrigger
  | MarketingRefundReturnSubmittedTrigger;

export interface OrderTrackingTrigger extends BaseTrigger {
  category: "Order tracking";
  key: OrderTrackingTriggerKey;
}
export interface ReturnTrackingTrigger extends BaseTrigger {
  category: "Return tracking";
  key: ReturnTrackingTriggerKey;
}

export interface BoxlessReturnsReturnTrackingTrigger extends BaseTrigger {
  category: "Return tracking";
  key: ReturnTrackingTriggerKey.BOXLESS_RETURNS_RMA_CREATED;
}

export interface TnplTrigger extends BaseTrigger {
  category: "Try first";
  key: TnplTriggerKey;
}
export interface ReviewsTrigger extends BaseTrigger {
  category: "Reviews";
  key: ReviewsTriggerKey;
}
export interface OmsTrigger extends BaseTrigger {
  category: "Oms";
  key: OmsTriggerKey;
}
export interface CheckoutOptimizationTrigger extends BaseTrigger {
  category: "Checkout personalization";
  key: CheckoutOptimizationTriggerKey;
}
export interface CheckoutParcelTrigger extends BaseTrigger {
  category: "Checkout package";
  key: CheckoutParcelTriggerKey;
}
export interface UpsellTrigger extends BaseTrigger {
  category: "Upsell";
  key: OrderTrackingTriggerKey.POST_PURCHASE_UPSELL;
}
export interface PostPurchaseTrigger extends BaseTrigger {
  category: "Post-purchase";
  key: PostPurchaseTriggerKey;
}
export interface TrackingPageTrigger extends BaseTrigger {
  category: "Tracking page";
  key: TrackingPageTriggerKey;
}
export interface EwTrigger extends BaseTrigger {
  category: "Extended warranty";
  key: EwTriggerKey;
}

export interface CustomEventTrigger extends BaseTrigger {
  category: "Custom Event";
  key: CustomEventTriggerKey;
}

export interface RulesTrigger extends BaseTrigger {
  category: "Rules";
  key: RuleTriggerType;
}

export type Trigger =
  | MarketingTrigger
  | OrderTrackingTrigger
  | ReturnTrackingTrigger
  | TnplTrigger
  | ReviewsTrigger
  | OmsTrigger
  | CheckoutOptimizationTrigger
  | CheckoutParcelTrigger
  | UpsellTrigger
  | PostPurchaseTrigger
  | TrackingPageTrigger
  | EwTrigger
  | YotpoIntegrationTrigger
  | RechargeIntegrationTrigger
  | CustomEventTrigger
  | RulesTrigger;

// For every trigger key, there should be a trigger type
// in this file. This function expression enforces that.
// eslint-disable-next-line no-unused-expressions
() => {
  const a = "" as unknown as XorOfUnions<Trigger["key"], TriggerKey>;
  a satisfies never;
};

// Exhaustive matching on SchemaType
type SchemaTypeConfig = {
  [K in SchemaType]: {
    category: Category;
    displayName: string;
    isEnabled: (settings: Team["settings"]) => boolean;
  };
};

const schemaTypeConfigs: SchemaTypeConfig = {
  [SchemaType.ORDER_TRACKING]: {
    category: "Order tracking",
    displayName: "Order tracking",
    isEnabled: (settings) => settings.orderTracking?.enabled ?? false,
  },
  [SchemaType.RETURN_TRACKING]: {
    category: "Return tracking",
    displayName: "Return tracking",
    isEnabled: (settings) => settings.returns?.enabled ?? false,
  },
  [SchemaType.TNPL_TRACKING]: {
    category: "Try first",
    displayName: "Try first",
    isEnabled: (settings) => settings.tnpl?.enabled ?? false,
  },
  [SchemaType.EMAIL_MARKETING_SIGNUP]: {
    category: "Marketing",
    displayName: "Email marketing sign-up",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.SMS_MARKETING_SIGNUP]: {
    category: "Marketing",
    displayName: "SMS marketing sign-up",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_CART_ABANDONMENT]: {
    category: "Marketing",
    displayName: "Cart abandonment",
    isEnabled: (settings) =>
      (settings.marketing?.enabled && !!settings.shopifyPixelId) ?? false,
  },
  [SchemaType.MARKETING_BROWSE_ABANDONMENT]: {
    category: "Marketing",
    displayName: "Browse abandonment",
    isEnabled: (settings) =>
      (settings.marketing?.enabled && !!settings.shopifyPixelId) ?? false,
  },
  [SchemaType.MARKETING_CHECKOUT_ABANDONMENT]: {
    category: "Marketing",
    displayName: "Checkout abandonment",
    isEnabled: (settings) =>
      (settings.marketing?.enabled && !!settings.shopifyPixelId) ?? false,
  },
  [SchemaType.MARKETING_COMMENTSOLD_CART_ABANDONMENT]: {
    category: "Marketing",
    displayName: "CommentSold cart abandonment",
    isEnabled: (settings) => false,
  },
  [SchemaType.MARKETING_COMMENTSOLD_BROWSE_ABANDONMENT]: {
    category: "Marketing",
    displayName: "CommentSold browse abandonment",
    isEnabled: (settings) => false,
  },
  [SchemaType.MARKETING_COMMENTSOLD_CHECKOUT_ABANDONMENT]: {
    category: "Marketing",
    displayName: "CommentSold checkout abandonment",
    isEnabled: (settings) => false,
  },
  [SchemaType.MARKETING_SEGMENT_MEMBERSHIP_CHANGE]: {
    category: "Marketing",
    displayName: "Segment membership change",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.REVIEWS]: {
    category: "Reviews",
    displayName: "Reviews",
    isEnabled: (settings) => settings.reviews?.enabled ?? false,
  },
  [SchemaType.OMS_NEW_FULFILLMENT_GROUP]: {
    category: "Oms",
    displayName: "New shipment",
    isEnabled: (settings) => settings.outboundLabels?.newOmsEnabled ?? false,
  },
  [SchemaType.OMS_ORDER_READY_FOR_PICKUP]: {
    category: "Oms",
    displayName: "OMS pickup order ready",
    isEnabled: (settings) =>
      (settings.outboundLabels?.newOmsEnabled &&
        settings.orderTracking?.enabled) ??
      false,
  },
  [SchemaType.ADDRESS_VERIFICATION_FAILED]: {
    category: "Oms",
    displayName: "OMS address verification failed",
    isEnabled: (settings) =>
      (settings.outboundLabels?.newOmsEnabled &&
        settings.orderTracking?.enabled) ??
      false,
  },
  [SchemaType.MARKETING_BACK_IN_STOCK]: {
    category: "Marketing",
    displayName: "Back in stock",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_LOW_INVENTORY]: {
    category: "Marketing",
    displayName: "Low inventory",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_WARRANTY_REGISTRATION]: {
    category: "Marketing",
    displayName: "Warranty registration",
    isEnabled: (settings) =>
      // TODO: Deprecated, remove after migration
      settings.warranties?.registrationRewardType ===
        RegistrationRewardType.EMAIL_DISCOUNT ||
      settings.warranties?.triggerMarketingFlow,
  },
  [SchemaType.MARKETING_CAMPAIGN]: {
    category: "Marketing",
    displayName: "Marketing campaign",
    isEnabled: () => false, // Not used
  },
  [SchemaType.MARKETING_EMAIL]: {
    category: "Marketing",
    displayName: "Marketing email",
    isEnabled: () => false, // Not used
  },
  [SchemaType.CHECKOUT_TREE]: {
    category: "Checkout personalization",
    displayName: "Checkout visit",
    isEnabled: () => false, // Not used
  },
  [SchemaType.RCO_CHECKOUT_PACKAGE]: {
    category: "Checkout package",
    displayName: "Checkout visit",
    isEnabled: () => false, // Not used
  },
  [SchemaType.MARKETING_DATE]: {
    category: "Marketing",
    displayName: "Marketing date",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_PRICE_DROP]: {
    category: "Marketing",
    displayName: "Price drop",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.OOS_EXCHANGE_RESTOCKED]: {
    category: "Marketing",
    displayName: "Return recovery",
    isEnabled: (settings) =>
      (settings.returns?.enabled && settings.marketing?.enabled) ?? false,
  },
  [SchemaType.REFUND_RETURN_SUBMITTED]: {
    category: "Marketing",
    displayName: "Refund return submitted",
    isEnabled: (settings) =>
      (settings.returns?.enabled && settings.marketing?.enabled) ?? false,
  },
  [SchemaType.POST_PURCHASE_FLOW]: {
    category: "Post-purchase",
    displayName: "Checkout completed",
    isEnabled: () => false, // Not used
  },
  [SchemaType.TRACKING_PAGE_FLOW]: {
    category: "Order tracking",
    displayName: "Tracking page view",
    isEnabled: () => false, // Not used - tracking pages have their own flow system
  },
  [SchemaType.PRODUCT_SUBSCRIPTION_UPCOMING_CHARGE]: {
    category: "Marketing",
    displayName: "Product subscription",
    // TODO: this won't work unless they have a Recharge integration. Maybe deserves its own setting.
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.EXTENDED_WARRANTY]: {
    category: "Extended warranty",
    displayName: "Extended warranty purchased",
    isEnabled: (settings) => settings.extendedWarranties?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_EXPIRATION_REMINDER]: {
    category: "Integration",
    displayName: "Yotpo loyalty expiration reminder",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_POINTS_REMINDER]: {
    category: "Integration",
    displayName: "Yotpo loyalty points reminder",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_REDEMPTION_REMINDER]: {
    category: "Integration",
    displayName: "Yotpo loyalty redemption reminder",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_REDEMPTION_CREATED]: {
    category: "Integration",
    displayName: "Yotpo loyalty redemption created",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_REFERRAL_COMPLETED]: {
    category: "Integration",
    displayName: "Yotpo loyalty referral completed",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_REFERRAL_SHARED]: {
    category: "Integration",
    displayName: "Yotpo loyalty referral shared",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_CUSTOMER_BIRTHDAY]: {
    category: "Integration",
    displayName: "Yotpo loyalty customer birthday",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_OPT_IN]: {
    category: "Integration",
    displayName: "Yotpo loyalty opt in",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_TIER_EARNED]: {
    category: "Integration",
    displayName: "Yotpo loyalty tier earned",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_TIER_LOST]: {
    category: "Integration",
    displayName: "Yotpo loyalty tier lost",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_POINTS_EARNED]: {
    category: "Integration",
    displayName: "Yotpo loyalty points earned",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.CUSTOM_EVENT]: {
    category: "Custom Event",
    displayName: "Custom event",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.RULES]: {
    category: "Rules",
    displayName: "Rules",
    isEnabled: (settings) => settings.support?.enabled ?? false,
  },
  [SchemaType.MARKETING_AGENT]: {
    category: "Marketing",
    displayName: "Marketing agent",
    isEnabled: () => false, // Never shown in UI - used programmatically only
  },
};

export function categorySchemaPermutations(
  settings: Team["settings"],
): { category: Category; schemaType: SchemaType; displayName: string }[] {
  const permutations: {
    category: Category;
    schemaType: SchemaType;
    displayName: string;
  }[] = [];

  // Add all enabled schema types
  for (const [schemaType, config] of Object.entries(schemaTypeConfigs)) {
    if (config.isEnabled(settings)) {
      permutations.push({
        category: config.category,
        schemaType: schemaType as SchemaType,
        displayName: config.displayName,
      });
    }
  }

  // Special case for ORDER_TRACKING when marketing is enabled but order tracking isn't
  if (settings.marketing?.enabled && !settings.orderTracking?.enabled) {
    permutations.push({
      category: "Order tracking",
      schemaType: SchemaType.ORDER_TRACKING,
      displayName: "Order created",
    });
  }

  return permutations;
}

const orderTrackingTriggers: OrderTrackingTrigger[] = [
  {
    key: OrderTrackingTriggerKey.ORDER_CREATED,
    displayName: "Order created",
    description:
      "Fires immediately when customer completes purchase and order is created",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_FULFILLED,
    displayName: "Order fulfilled",
    description:
      "Fires when merchant marks order as fulfilled (items packed and ready to ship)",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_PRE_TRANSIT,
    displayName: "Order pre-transit",
    description:
      "Shipping label created but carrier hasn't picked up package yet",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_IN_TRANSIT,
    displayName: "Order in-transit",
    description:
      "Carrier picks up package and it begins moving through shipping network",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_OUT_FOR_DELIVERY,
    displayName: "Order out for delivery",
    description:
      "Package loaded on delivery vehicle for final delivery attempt",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_DELIVERED,
    displayName: "Order delivered",
    description:
      "Carrier confirms package was successfully delivered to customer",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_AVAILABLE_FOR_PICKUP,
    displayName: "Order available for pickup",
    description:
      "Pickup order is ready and customer can collect it from store/location",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_AVAILABLE_FOR_PICKUP_CARRIER,
    displayName: "Order available for pickup (carrier)",
    description:
      "Carrier reports package is available for pickup at carrier facility",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_STALLED_IN_TRANSIT,
    displayName: "Order stalled in transit",
    description:
      "Package hasn't moved locations for extended period during shipping",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_STALLED_IN_FULFILLMENT,
    displayName: "Order stalled in fulfillment",
    description:
      "Order hasn't been fulfilled within expected timeframe after purchase",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_DELAYED,
    displayName: "Order delayed",
    description: "Delivery date pushed back from original estimate",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_ARRIVING_EARLY,
    displayName: "Order arriving early",
    description: "Package will arrive ahead of original delivery estimate",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_RETURN_TO_SENDER,
    displayName: "Order return to sender",
    description: "Package being returned to merchant due to failed delivery",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_DELIVERY_ATTEMPTED,
    displayName: "Order delivery attempted",
    description: "Carrier attempted delivery but customer wasn't available",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_DELIVERY_FAILURE,
    displayName: "Order delivery failure",
    description:
      "Delivery permanently fails after multiple unsuccessful attempts",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_SHIPMENT_CANCELLED,
    displayName: "Order shipment cancelled",
    description: "Shipment cancelled before delivery (by merchant or carrier)",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_SHIPMENT_ERROR,
    displayName: "Order shipment error",
    description:
      "Carrier reports error with shipment requiring attention/resolution",
  },
  {
    key: OrderTrackingTriggerKey.POST_PURCHASE_UPSELL,
    displayName: "Post purchase upsell",
    description: "Fires to trigger upsell opportunities after order completion",
  },
].map((t) => ({
  ...t,
  category: "Order tracking" as const,
  schemaType: SchemaType.ORDER_TRACKING,
}));

export const returnTrackingTriggers: ReturnTrackingTrigger[] = [
  {
    key: ReturnTrackingTriggerKey.RETURN_REQUEST_CREATED,
    displayName: "Return request created",
    description:
      "Fires when customer submits a return request through the return portal",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_IN_TRANSIT,
    displayName: "Return in-transit",
    description:
      "Customer's return package picked up and moving toward merchant",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_OUT_FOR_DELIVERY,
    displayName: "Return out for delivery",
    description:
      "Return package loaded for final delivery to merchant facility",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_DELIVERED,
    displayName: "Return received", // renamed for UI clarity
    description: "Merchant receives and scans in the returned package",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_STALLED,
    displayName: "Return stalled in transit",
    description: "Return package hasn't moved locations for extended period",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_DELAYED,
    displayName: "Return delayed",
    description: "Return delivery to merchant delayed from original estimate",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_ARRIVING_EARLY,
    displayName: "Return arriving early",
    description: "Return package will arrive at merchant ahead of schedule",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_RETURN_TO_SENDER,
    displayName: "Return return to sender",
    description:
      "Return package being sent back to customer due to delivery issues",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_DELIVERY_ATTEMPTED,
    displayName: "Return delivery attempted",
    description: "Carrier attempted to deliver return to merchant but failed",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_DELIVERY_FAILURE,
    displayName: "Return delivery failure",
    description: "Return delivery permanently fails after multiple attempts",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_SHIPMENT_CANCELLED,
    displayName: "Return shipment cancelled",
    description: "Return shipment cancelled before reaching merchant",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_SHIPMENT_ERROR,
    displayName: "Return shipment error",
    description:
      "Carrier reports error with return shipment needing resolution",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_APPROVED,
    displayName: "Return approved",
    description:
      "Merchant approves return request after reviewing items/policy or return request is automatically approved",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_PROCESSED,
    displayName: "Return processed",
    description:
      "Merchant completes return processing or return is automatically processed (refund issued, exchange sent)",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_REJECTED,
    displayName: "Return rejected",
    description:
      "Merchant rejects return request due to policy violations or return request is automatically rejected",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_IN_REVIEW,
    displayName: "Return in review",
    description: "Returned items being inspected/reviewed by merchant",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_PICKUP_REMINDER,
    displayName: "Return pickup reminder",
    description: "Remind customer about scheduled return pickup",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_PICKUP_SUCCESS,
    displayName: "Return pickup success",
    description: "Return pickup successfully completed by carrier",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_PICKUP_FAILURE,
    displayName: "Return pickup failure",
    description: "Scheduled return pickup fails or customer unavailable",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_SHIPMENT_REMINDER,
    displayName: "Return shipment reminder",
    description: "Remind customer to ship their return if not yet sent",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_OUSTANDING_BALANCE,
    displayName: "Return outstanding balance",
    description:
      "Customer owes additional payment due to return/exchange price difference",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_EXPIRED,
    displayName: "Return expired",
    description:
      "Return window expires without customer completing return process",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_EXCHANGE_OUT_OF_STOCK,
    displayName: "Return exchange out of stock",
    description:
      "Requested exchange item becomes unavailable during return process",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_EXCHANGE_COMPLETED,
    displayName: "Exchange payment completed",
    description:
      "Customer has paid the outstanding balance for an exchange order",
  },
  {
    key: ReturnTrackingTriggerKey.BOXLESS_RETURNS_RMA_CREATED,
    displayName: "Boxless returns RMA created",
    description:
      "Return shipping label created but package hasn't been picked up yet",
  },
].map((t) => ({
  ...t,
  category: "Return tracking" as const,
  schemaType: SchemaType.RETURN_TRACKING,
}));

const tnplTriggers: TnplTrigger[] = [
  {
    key: TnplTriggerKey.TRIAL_STARTED,
    displayName: "Try First trial started",
    description: "Customer begins try-now-pay-later trial period",
  },
  {
    key: TnplTriggerKey.ABOUT_TO_REAUTH_CUSTOMER,
    displayName: "Try First about to reauthorize customer payment",
    description: "Customer payment needs reauthorization for trial items",
  },
  {
    key: TnplTriggerKey.REAUTHORIZATION_FAILED,
    displayName: "Try First reauthorization failed",
    description: "Customer payment reauthorization fails for trial items",
  },
  {
    key: TnplTriggerKey.TRIAL_ITEMS_CANCELLED,
    displayName: "Try First trial items cancelled",
    description: "Customer cancels specific items during trial period",
  },
  {
    key: TnplTriggerKey.TRIAL_ENDED,
    displayName: "Try First trial ended",
    description: "Try-first trial period concludes (items kept or returned)",
  },
].map((t) => ({
  ...t,
  category: "Try first" as const,
  schemaType: SchemaType.TNPL_TRACKING,
}));

const marketingTriggers: MarketingTrigger[] = [
  {
    key: MarketingTriggerKey.EMAIL_SIGNUP,
    displayName: "Marketing email signup",
    category: "Marketing",
    schemaType: SchemaType.EMAIL_MARKETING_SIGNUP,
    description:
      "Customer subscribes to email marketing via forms, popups, or widgets",
  },
  {
    key: MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY,
    displayName: "Marketing email signup through Shopify",
    category: "Marketing",
    schemaType: SchemaType.EMAIL_MARKETING_SIGNUP,
    description:
      "Customer subscribes via Shopify's native marketing acceptance checkbox",
  },
  {
    key: MarketingTriggerKey.SMS_SIGNUP,
    displayName: "Marketing SMS signup",
    category: "Marketing",
    schemaType: SchemaType.SMS_MARKETING_SIGNUP,
    description:
      "Customer subscribes to SMS marketing via forms, popups, or widgets",
  },
  {
    key: MarketingTriggerKey.CART_ABANDONED,
    displayName: "Shopify abandoned cart",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_CART_ABANDONMENT,
    description:
      "Customer adds items to cart but doesn't start checkout within set timeframe",
  },
  {
    key: MarketingTriggerKey.BROWSE_ABANDONED,
    displayName: "Shopify abandoned browse",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_BROWSE_ABANDONMENT,
    description:
      "Customer views products but doesn't add anything to cart within timeframe",
  },
  {
    key: MarketingTriggerKey.CHECKOUT_ABANDONED,
    displayName: "Shopify abandoned checkout",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_CHECKOUT_ABANDONMENT,
    description:
      "Customer starts checkout process but doesn't complete payment",
  },
  {
    key: MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED,
    displayName: "CommentSold abandoned cart",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_COMMENTSOLD_CART_ABANDONMENT,
    description:
      "CommentSold customer adds items to cart but doesn't start checkout within set timeframe",
  },
  {
    key: MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED,
    displayName: "CommentSold abandoned browse",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_COMMENTSOLD_BROWSE_ABANDONMENT,
    description:
      "CommentSold customer views products but doesn't add anything to cart within timeframe",
  },
  {
    key: MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED,
    displayName: "CommentSold abandoned checkout",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_COMMENTSOLD_CHECKOUT_ABANDONMENT,
    description:
      "CommentSold customer starts checkout process but doesn't complete payment",
  },
  {
    key: MarketingTriggerKey.BACK_IN_STOCK,
    displayName: "Back in stock",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_BACK_IN_STOCK,
    description:
      "Previously out-of-stock product variant becomes available again",
  },
  {
    key: MarketingTriggerKey.CUSTOMER_GROUP_ENTERED,
    displayName: "Segment entered",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_SEGMENT_MEMBERSHIP_CHANGE,
    description:
      "Customer meets criteria and enters a defined marketing segment/group",
  },
  {
    key: MarketingTriggerKey.CUSTOMER_GROUP_EXITED,
    displayName: "Segment exited",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_SEGMENT_MEMBERSHIP_CHANGE,
    description:
      "Customer no longer meets criteria and exits a marketing segment",
  },
  {
    key: MarketingTriggerKey.WARRANTY_REGISTRATION,
    displayName: "Warranty registration",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_WARRANTY_REGISTRATION,
    description: "Customer registers product warranty and opts into marketing",
  },
  {
    key: MarketingTriggerKey.LOW_INVENTORY,
    displayName: "Low inventory",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_LOW_INVENTORY,
    description: "Product variant inventory drops below threshold",
  },
  {
    key: MarketingTriggerKey.DATE,
    displayName: "Date",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_DATE,
    description:
      "Fires on specific dates based on customer characteristics (e.g., birthdays)",
  },
  {
    key: MarketingTriggerKey.PRICE_DROP,
    displayName: "Price drop",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_PRICE_DROP,
    description: "Product price drops below customer's watchlist threshold",
  },
  {
    key: MarketingTriggerKey.OOS_EXCHANGE_RESTOCKED,
    displayName: "OOS exchange restocked",
    category: "Marketing",
    schemaType: SchemaType.OOS_EXCHANGE_RESTOCKED,
    description:
      "Fires when a product variant that a customer wanted to exchange for (but was out of stock) becomes available again.",
  },
  {
    key: MarketingTriggerKey.REFUND_RETURN_SUBMITTED,
    displayName: "Refund return submitted",
    category: "Marketing",
    schemaType: SchemaType.REFUND_RETURN_SUBMITTED,
    description:
      "Fires when a customer submits a return for refund, with AI product recommendations.",
  },
];

const reviewsTriggers: ReviewsTrigger[] = [
  {
    key: ReviewsTriggerKey.REVIEW_SUBMITTED,
    displayName: "Review submitted",
    category: "Reviews",
    schemaType: SchemaType.REVIEWS,
    description: "Customer submits product review",
  },
];

const omsTriggers: OmsTrigger[] = [
  {
    key: OmsTriggerKey.NEW_FULFILLMENT_GROUP,
    displayName: "New shipment",
    category: "Oms",
    schemaType: SchemaType.OMS_NEW_FULFILLMENT_GROUP,
    description: "New shipment/fulfillment group created in OMS",
  },
  {
    key: OmsTriggerKey.ORDER_READY_FOR_PICKUP,
    displayName: "OMS pickup order ready",
    category: "Oms",
    schemaType: SchemaType.OMS_ORDER_READY_FOR_PICKUP,
    description: "Pickup order is ready at store location",
  },
  {
    key: OmsTriggerKey.ADDRESS_VERIFICATION_FAILED,
    displayName: "Address verification failed",
    category: "Oms",
    schemaType: SchemaType.ADDRESS_VERIFICATION_FAILED,
    description: "Shipping address fails verification checks",
  },
];

const checkoutOptimizationTriggers: CheckoutOptimizationTrigger[] = [
  {
    key: CheckoutOptimizationTriggerKey.CHECKOUT_VISIT,
    displayName: "Checkout visit",
    category: "Checkout personalization",
    schemaType: SchemaType.CHECKOUT_TREE,
    description: "Customer visits checkout page (for optimization flows)",
  },
];

const checkoutParcelTriggers: CheckoutParcelTrigger[] = [
  {
    key: CheckoutParcelTriggerKey.CHECKOUT_VISIT_PARCEL,
    displayName: "Checkout",
    category: "Checkout package",
    schemaType: SchemaType.RCO_CHECKOUT_PACKAGE,
    description: "Customer visits checkout with parcel/package enhancements",
  },
];

const postPurchaseTriggers: PostPurchaseTrigger[] = [
  {
    key: PostPurchaseTriggerKey.ORDER_COMPLETED,
    displayName: "Order completed",
    category: "Post-purchase",
    schemaType: SchemaType.POST_PURCHASE_FLOW,
    description: "Fires when checkout/purchase process is fully completed",
  },
];

const trackingPageTriggers: TrackingPageTrigger[] = [
  {
    key: TrackingPageTriggerKey.TRACKING_PAGE_VIEW,
    displayName: "Tracking page view",
    category: "Tracking page",
    schemaType: SchemaType.TRACKING_PAGE_FLOW,
    description: "Fires when a customer views the order tracking page",
  },
];

const ewTriggers: EwTrigger[] = [
  {
    key: EwTriggerKey.EXTENDED_WARRANTY_PURCHASED,
    displayName: "Extended warranty purchased",
    category: "Extended warranty",
    schemaType: SchemaType.EXTENDED_WARRANTY,
    description: "Customer purchases extended warranty for their product",
  },
];

const customEventTriggers: CustomEventTrigger[] = [
  {
    key: CustomEventTriggerKey.CUSTOM_EVENT,
    displayName: "Custom event",
    category: "Custom Event",
    schemaType: SchemaType.CUSTOM_EVENT,
    description:
      "Fires when a custom event is triggered via API with merchant-defined event names and properties",
  },
];

export const rulesTriggers: RulesTrigger[] = [
  {
    key: RuleTriggerType.CONVERSATION_CREATED,
    displayName: "Conversation created",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a new conversation is created",
  },
  {
    key: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
    displayName: "New customer message",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a new customer message is added to a conversation",
  },
  {
    key: RuleTriggerType.MERCHANT_MESSAGE_CREATED,
    displayName: "New merchant message",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a new merchant message is added to a conversation",
  },
  {
    key: RuleTriggerType.MESSAGE_RECEIVED,
    displayName: "Message received",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a message is received in a conversation",
  },
  {
    key: RuleTriggerType.CONVERSATION_STATUS_CHANGED,
    displayName: "Conversation status changed",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a conversation's status changes",
  },
  {
    key: RuleTriggerType.TAG_ADDED,
    displayName: "Tag added",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a tag is added to a conversation",
  },
  {
    key: RuleTriggerType.ASSIGNMENT_CHANGED,
    displayName: "Assignment changed",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a conversation's assignment changes",
  },
  {
    key: RuleTriggerType.CHAT_CONVERTED_TO_EMAIL,
    displayName: "Chat converted to email",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a chat conversation is converted to email",
  },
];

export const triggers = [
  ...orderTrackingTriggers,
  ...returnTrackingTriggers,
  ...tnplTriggers,
  ...marketingTriggers,
  ...reviewsTriggers,
  ...omsTriggers,
  ...checkoutOptimizationTriggers,
  ...checkoutParcelTriggers,
  ...postPurchaseTriggers,
  ...trackingPageTriggers,
  ...ewTriggers,
  ...yotpoIntegrationTriggers,
  ...rechargeIntegrationTriggers,
  ...customEventTriggers,
  ...rulesTriggers,
];

export function getTriggerDisplayName(
  key: TriggerKey | undefined,
): string | undefined {
  return triggers.find((t) => t.key === key)?.displayName;
}

export function getEnabledTriggers(
  team: Team,
  enabledIntegrations: Record<Integration, boolean>,
  availableProviders: Provider[],
) {
  const hasAnyIntegration = Object.values(enabledIntegrations).some(Boolean);

  const categories: Trigger["category"][] = [];
  if (team.settings.orderTracking?.enabled) {
    categories.push("Order tracking");
  }
  if (team.settings.returns?.enabled) {
    categories.push("Return tracking");
  }
  if (team.settings.marketing?.enabled) {
    categories.push("Marketing");
  }
  if (team.settings.reviews?.enabled) {
    categories.push("Reviews");
  }
  if (team.settings.outboundLabels?.newOmsEnabled) {
    categories.push("Oms");
  }
  if (team.settings.tnpl?.enabled) {
    categories.push("Try first");
  }
  if (team.settings.extendedWarranties?.enabled) {
    categories.push("Extended warranty");
  }
  if (team.settings.marketing?.enabled && hasAnyIntegration) {
    categories.push("Integration");
  }
  if (team.settings.marketing?.enabled) {
    categories.push("Custom Event");
  }
  // This function is used for marketing automations, so we don't need to
  // include checkout personalization triggers here. Adding OMS was probably a
  // mistake.
  const enabledTriggers = triggers.filter((trigger) =>
    categories.includes(trigger.category),
  );

  // special case
  if (
    team.settings.marketing?.enabled &&
    !team.settings.orderTracking?.enabled
  ) {
    enabledTriggers.push({
      key: OrderTrackingTriggerKey.ORDER_CREATED,
      displayName: "Order created",
      category: "Order tracking",
      schemaType: SchemaType.ORDER_TRACKING,
      description:
        "Fires immediately when customer completes purchase and order is created",
    });
  }

  const hasCommentSoldProvider = availableProviders.includes(
    Provider.COMMENTSOLD,
  );

  const filteredTriggers = enabledTriggers.filter((trigger) => {
    const isCommentSoldTrigger =
      trigger.key === MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED ||
      trigger.key === MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED ||
      trigger.key === MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED;

    if (isCommentSoldTrigger) {
      return hasCommentSoldProvider;
    }

    if (trigger.category === "Integration") {
      return enabledIntegrations[trigger.integration];
    }

    return true;
  });

  return filteredTriggers;
}

export function getTriggerName(key: TriggerKey) {
  return triggers.find((t) => t.key === key)?.displayName;
}
